import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppState } from '../../context/LoadTestContext';
import {
  CartesianGrid,
  Customized,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Box,
  Button,
  Skeleton,
  Typography,
} from '@mui/material';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { useTheme } from '@mui/material/styles';
import { fitAxis, zeroAnchoredAxis } from './axisScale';
import { useUnits } from '../units';
import { smoothRows } from '../smoothing';
import { TEST_LOADS } from '../calibrationSettings';

// Dashed guides on the load axis, in kips (converted for metric). Only drawn
// until the test has loads of its own to show (see loadLines below).
const REFERENCE_LOADS_KIPS = [100, 200, 1248];
// The hint and Reset button sit in the plot's bottom-left corner (just right of
// the deflection axis): low load with large deflection, which a settling curve
// never reaches, so they don't cover the line.
const ZOOM_CONTROL_LEFT = 74;
// A drag thinner than this (px) in one direction zooms the other axis only, so
// a sideways swipe still zooms load like before; smaller than this both ways is
// a click, not a zoom.
const BAND_PX = 10;

// The test load names: drawn at the top of the plot, just left of their line.
const LABEL_FONT_SIZE = 11;
const LABEL_ROW_PX = 13;
const LABEL_INSET_PX = 5;
const LABEL_GAP_PX = 6;

let measureContext = null;
function textWidth(text, font) {
  try {
    measureContext = measureContext || document.createElement('canvas').getContext('2d');
    measureContext.font = font;
    return measureContext.measureText(text).width;
  } catch {
    return text.length * LABEL_FONT_SIZE * 0.65;
  }
}

// Rendered through <Customized>, which hands it the chart's axes and plot area.
// Every name sits on the top row; one only drops a row when it would run into
// a name already on that row, so close loads stay readable without stacking
// names that have room.
function LoadLineLabels({ xAxisMap, offset, lines, fontFamily }) {
  const scale = xAxisMap && Object.values(xAxisMap)[0]?.scale;
  if (!scale || !offset) return null;
  const font = `600 ${LABEL_FONT_SIZE}px ${fontFamily}`;
  const left = offset.left;
  const right = offset.left + offset.width;
  const rowEnds = []; // the right edge of the last name placed on each row
  const placed = lines
    .map((line) => ({ ...line, px: scale(line.x) }))
    .filter((line) => Number.isFinite(line.px) && line.px >= left && line.px <= right) // zoomed off the plot
    .sort((a, b) => a.px - b.px)
    .map((line) => {
      const end = line.px - LABEL_INSET_PX;
      const start = end - textWidth(line.short, font);
      let row = rowEnds.findIndex((rowEnd) => rowEnd + LABEL_GAP_PX <= start);
      if (row === -1) row = rowEnds.length;
      rowEnds[row] = end;
      return { ...line, end, row };
    });
  return (
    <g pointerEvents='none'>
      {placed.map((line) => (
        <text
          key={line.key}
          x={line.end}
          y={offset.top + LABEL_INSET_PX + line.row * LABEL_ROW_PX}
          dy='0.71em'
          textAnchor='end'
          fontSize={LABEL_FONT_SIZE}
          fontWeight={600}
          fill={line.fill}
        >
          {line.short}
        </text>
      ))}
    </g>
  );
}


// marker (optional): { loadKips, deflectionIn, label } — a point to ring on the
// curve, e.g. the end of the step picked in the new dashboard's step strip.
function LoadGraph({ marker = null } = {}) {
  const { loading, loadGraphData, selectedBox, calibration, allStepSummaryData, showUnloadSteps, smoothing, measurementUnits } = useAppState();

  const theme = useTheme();
  const units = useUnits();

  const calibratedLoad = ['kips', 'kN'].includes(units.forceUnit);
  const calibratedLvdt = measurementUnits?.lvdt_graph === 'in';
  const canPlotDeflection = calibratedLoad && calibratedLvdt;

  const hiddenSteps = useMemo(() => new Set(
    showUnloadSteps
      ? []
      : (allStepSummaryData || [])
          .filter((s) => s.kind === 'U')
          .map((s) => Number(s.step))
  ), [allStepSummaryData, showUnloadSteps]);

  const points = useMemo(() => {
    // if (!calibratedLoad) return [];
    if (!canPlotDeflection) return [];

    // Split at hidden steps so smoothing cannot cross those gaps.
    const stretches = [[]];

    (Array.isArray(loadGraphData) ? loadGraphData : []).forEach((d) => {
      if (hiddenSteps.has(Number(d?.step))) {
        if (stretches[stretches.length - 1].length) stretches.push([]);
        return;
      }

      stretches[stretches.length - 1].push({
        step: d?.step,
        load: d?.Loadcell_kips == null
          ? null
          : units.toForce(Number(d.Loadcell_kips)),
        deflection: d?.deflection == null
          ? null
          : units.toLength(Number(d.deflection)),
      });
    });

    const out = [];

    stretches.filter((stretch) => stretch.length).forEach((stretch) => {
      const smoothed = smoothRows(stretch, ['load', 'deflection'], smoothing);

      // A null deflection prevents a line across hidden steps.
      if (out.length) {
        out.push({ load: smoothed[0].load, deflection: null });
      }

      out.push(...smoothed);
    });

    return out;
  }, [
    loadGraphData,
    hiddenSteps,
    smoothing,
    units.system,
    units.forceUnit,
    canPlotDeflection
  ]);

  const hasPlotData = canPlotDeflection && points.some((point) => Number.isFinite(point.load) && Number.isFinite(point.deflection));

  const emptyMessage = !canPlotDeflection ? 'Calibrate the load cell and at least one LVDT to plot Load vs Deflection.' : !hasPlotData ? 'No valid load and deflection readings to plot.' : null;

  // The test's design, test and max loads (entered in the Calibration window,
  // in kips) as lines on the load axis, each in its own colour with a short
  // name on it. Unset ones are left out.
  const loadLines = useMemo(() => {
    if (!calibratedLoad) return [];

    return TEST_LOADS
      .map((load) => ({ ...load, kips: calibration?.loads?.[load.key], }))
      .filter((line) => (typeof line.kips === 'number' && Number.isFinite(line.kips) && line.kips > 0))
      .map((line) => ({ ...line, x: units.toForce(line.kips), }));
  }, [calibration, units.system, units.forceUnit, calibratedLoad]);

  // Box zoom. `drag` is the selection in progress, as pixel corners in the
  // chart; `zoom` is the applied { load, deflection } ranges, either of which
  // may be null (that axis isn't zoomed).
  const [drag, setDrag] = useState(null);
  const [zoom, setZoom] = useState(null);

  // The chart's current axis scales, captured each render by <Customized>, so
  // pointer pixels can be turned into load and deflection values. (Recharts
  // only reports the x value for a shared tooltip.)
  const scalesRef = useRef(null);
  const captureScales = useCallback(({ xAxisMap, yAxisMap }) => {
    const x = xAxisMap && Object.values(xAxisMap)[0]?.scale;
    const y = yAxisMap && Object.values(yAxisMap)[0]?.scale;
    scalesRef.current = x?.invert && y?.invert ? { x, y } : null;
    return null;
  }, []);

  // A different box is a different test: start it zoomed out.
  // Zoom ranges are in display units, so a unit flip starts zoomed out too.
  useEffect(() => {
    setZoom(null);
    setDrag(null);
  }, [selectedBox?.id, units.system, units.forceUnit]);

  const loadAxis = useMemo(() => (zoom?.load ? fitAxis(zoom.load) : null), [zoom]);

  // Zoomed out, the load axis reaches the furthest test load as well as the
  // readings, so a load the test hasn't got to yet still has its line.
  const fullLoadAxis = useMemo(() => (loadLines.length
    ? fitAxis([0, ...points.map((d) => d.load), ...loadLines.map((line) => line.x)], { targetTicks: 7 })
    : null), [points, loadLines]);

  // Deflection axis follows the data: zero pinned at the top, round steps, and
  // room for however far the plate has settled (or rebounded above zero). A box
  // zoom fits the drawn deflection range; a load-only zoom fits just the
  // readings inside the selected load range.
  const deflectionAxis = useMemo(() => {
    if (zoom?.deflection) {
      const fitted = fitAxis(zoom.deflection, { minSpan: units.minZoomDeflectionSpan });
      if (fitted) return fitted;
    }
    if (loadAxis) {
      const [lo, hi] = loadAxis.domain;
      const inRange = points
        .filter((d) => d.load >= lo && d.load <= hi)
        .map((d) => d.deflection);
      const fitted = fitAxis(inRange, { minSpan: units.minZoomDeflectionSpan });
      if (fitted) return fitted;
    }
    return zeroAnchoredAxis(points.map((d) => d.deflection), { minSpan: units.minDeflectionSpan });
  }, [points, loadAxis, zoom, units.system]);

  // Pointer position in chart pixels; null outside the plot area.
  const pointerPx = (e) => (
    Number.isFinite(e?.chartX) && Number.isFinite(e?.chartY) ? { x: e.chartX, y: e.chartY } : null
  );

  // What a selection from `a` to `b` (pixels) would zoom: a box, or a band on
  // one axis when it's drawn nearly flat in the other direction.
  const selectionRanges = (a, b) => {
    const scales = scalesRef.current;
    if (!scales) return null;
    const wide = Math.abs(b.x - a.x) >= BAND_PX;
    const tall = Math.abs(b.y - a.y) >= BAND_PX;
    if (!wide && !tall) return null;
    const range = (scale, p, q) => {
      const v1 = scale.invert(p);
      const v2 = scale.invert(q);
      return [Math.min(v1, v2), Math.max(v1, v2)];
    };
    return {
      load: wide ? range(scales.x, a.x, b.x) : null,
      deflection: tall ? range(scales.y, a.y, b.y) : null,
    };
  };

  const handleDown = (e) => {
    const p = pointerPx(e);
    if (p) setDrag({ start: p, end: p });
  };

  const handleMove = (e) => {
    if (!drag) return;
    const p = pointerPx(e);
    if (p) setDrag((d) => (d ? { ...d, end: p } : d));
  };

  // Moves are throttled by the chart, so the last one can lag the pointer; the
  // release position is the true end of the selection.
  const handleUp = (e) => {
    if (drag) {
      const sel = selectionRanges(drag.start, pointerPx(e) ?? drag.end);
      // A load band narrower than the minimum span is too thin to be a zoom.
      if (sel?.load && sel.load[1] - sel.load[0] < units.minZoomLoadSpan) sel.load = null;
      if (sel && (sel.load || sel.deflection)) {
        // Zooming again inside a zoomed view keeps the other axis's range.
        setZoom((z) => ({
          load: sel.load ?? z?.load ?? null,
          deflection: sel.deflection ?? (sel.load ? null : z?.deflection ?? null),
        }));
      }
    }
    setDrag(null);
  };

  const preview = drag ? selectionRanges(drag.start, drag.end) : null;

  const resetZoom = () => {
    setZoom(null);
    setDrag(null);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '99%',
        minHeight: 180,
        userSelect: 'none',
        // Horizontal drags select a zoom range; vertical swipes still scroll the page.
        touchAction: 'pan-y',
      }}
    >
      {!loading && emptyMessage && (
        <Box
          role='status'
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 2,
            p: 3,
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 1,
              textAlign: 'center',
            }}
          >
            {emptyMessage}
          </Typography>
        </Box>
      )}
      {!loading && !emptyMessage && (
        zoom ? (
          <Button
            size='small'
            variant='contained'
            onClick={resetZoom}
            startIcon={<ZoomOutMapIcon sx={{ fontSize: 16 }} />}
            sx={{
              position: 'absolute',
              left: ZOOM_CONTROL_LEFT,
              bottom: 18,
              zIndex: 2,
              py: 0.25,
              px: 1,
              minWidth: 0,
              fontSize: 12,
              textTransform: 'none',
            }}
          >
            Reset zoom
          </Button>
        ) : (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ position: 'absolute', left: ZOOM_CONTROL_LEFT, bottom: 18, zIndex: 2, fontSize: 11, pointerEvents: 'none', opacity: 0.8 }}
          >
            Drag a box on the graph to zoom
          </Typography>
        )
      )}

      {loading ?
        <Skeleton variant='rectangle' animation='wave' fullWidth sx={{ borderRadius: 1, minHeight: 240 }} />
        :
        <ResponsiveContainer width='100%' height='99%' minHeight={180}>
          <LineChart
            data={points}
            margin={{ top: 5, right: 10, left: 5, bottom: 10 }}
            onMouseDown={handleDown}
            onMouseMove={handleMove}
            onMouseUp={handleUp}
            onMouseLeave={() => setDrag(null)}
            onDoubleClick={resetZoom}
          >
            <CartesianGrid strokeDasharray='3 3' />

            <XAxis
              dataKey='load'
              type='number'
              orientation='top'
              fontSize={10}
              {...(loadAxis
                ? { domain: loadAxis.domain, ticks: loadAxis.ticks, tickFormatter: loadAxis.format, interval: 0, allowDataOverflow: true }
                : fullLoadAxis
                  ? { domain: fullLoadAxis.domain, ticks: fullLoadAxis.ticks, tickFormatter: fullLoadAxis.format, interval: 0 }
                  : { tickCount: 7 })}
            >
              <Label value={`Load (${units.forceUnit})`} fontSize={12} dy={-15} />
            </XAxis>
            <YAxis
              dataKey='deflection'
              type='number'
              domain={deflectionAxis.domain}
              ticks={deflectionAxis.ticks}
              tickFormatter={deflectionAxis.format}
              interval={0}
              allowDataOverflow={Boolean(zoom)}
              fontSize={10}
            >
              <Label value={`Deflection (${units.lengthUnit})`} fontSize={12} verticalAnchor='middle' angle={-90} dx={-10} />
            </YAxis>

            <Tooltip
              contentStyle={{ backgroundColor: theme.chart.tooltipBg, color: theme.palette.text.secondary }}
              formatter={(value) => [`${Number(value).toFixed(units.lengthDecimals)} ${units.lengthUnit}`, 'Deflection']}
              labelFormatter={(load) => `${Number(load).toFixed(1)} ${units.forceUnit}`}
            />

            {hasPlotData && calibratedLoad && loadLines.length === 0 && REFERENCE_LOADS_KIPS.map((kips) => (
              <ReferenceLine key={kips} x={units.toForce(kips)} stroke='#999' strokeDasharray='3 3' />
            ))}

            {/* Their names sit at the top of the plot, left of the line (see
                LoadLineLabels): the curve is down in the deflection range by the
                time it reaches these loads. */}
            {hasPlotData && loadLines.map((line) => (
              <ReferenceLine
                key={line.key}
                x={line.x}
                stroke={theme.palette[line.color].main}
                strokeWidth={1.5}
                strokeDasharray={line.dash}
              />
            ))}
            {hasPlotData && loadLines.length > 0 && (
              <Customized
                component={(
                  <LoadLineLabels
                    lines={loadLines.map((line) => ({ ...line, fill: theme.palette[line.color].main }))}
                    fontFamily={theme.typography.fontFamily}
                  />
                )}
              />
            )}

            {/* <Legend align='right' verticalAlign='top' iconSize={10} iconType='plainline' wrapperStyle={{ fontSize: 12 }} /> */}

            <Line dataKey='deflection' stroke='#0ea5e9' dot={false} isAnimationActive={false}/>

            {hasPlotData && marker && Number.isFinite(marker.loadKips) && Number.isFinite(marker.deflectionIn) && (
              <ReferenceDot 
                x={units.toForce(marker.loadKips)}
                y={units.toLength(marker.deflection)}
                r={7}
                fill={theme.palette.background.paper}
                stroke={theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.secondary.main}
                strokeWidth={3}
                ifOverflow='hidden'
                label={marker.label ? { value: marker.label, position: 'right', fontSize: 11, fill: theme.palette.text.primary } : undefined}
              />
            )}
            {/* {calibratedLoad && marker && Number.isFinite(marker.loadKips) && Number.isFinite(marker.deflectionIn) && (
              <ReferenceDot
                x={units.toForce(marker.loadKips)}
                y={units.toLength(marker.deflectionIn)}
                r={7}
                fill={theme.palette.background.paper}
                stroke={theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.secondary.main}
                strokeWidth={3}
                ifOverflow='hidden'
                label={marker.label ? { value: marker.label, position: 'right', fontSize: 11, fill: theme.palette.text.primary } : undefined}
              />
            )} */}

            {/* A band (no y1/y2, or no x1/x2) spans the whole plot on that axis. */}
            {preview && (
              <ReferenceArea
                {...(preview.load ? { x1: preview.load[0], x2: preview.load[1] } : {})}
                {...(preview.deflection ? { y1: preview.deflection[0], y2: preview.deflection[1] } : {})}
                fill='#0ea5e9'
                fillOpacity={0.15}
                stroke='#0ea5e9'
                strokeOpacity={0.5}
                ifOverflow='hidden'
              />
            )}

            <Customized component={captureScales} />

          </LineChart>
        </ResponsiveContainer>
      }
    </Box>
  )
}

export default LoadGraph;
